import { useCallback, useState } from 'react'
import type React from 'react'
import cn from 'classnames'
import type { IconType } from 'react-icons'
import Modal from 'components/utils/Modal'
import CloseButton from 'components/utils/CloseButton'

import 'components/graph/controls/controls.less'
import 'components/graph/controls/valuePickerControl.less'

export type ValuePickerOption = {
  value: number
  label: string
  badge: string
}

type ValuePickerControlProps = {
  icon: IconType
  label: string
  options: ValuePickerOption[]
  value: number
  onSelect: (value: number) => void
}

const ValuePickerControl = ({ icon: Icon, label, options, value, onSelect }: ValuePickerControlProps) => {
  const [open, setOpen] = useState(false)
  const current = options.find((opt) => opt.value === value) ?? options[0]

  const handleOpen = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => setOpen(false), [])

  const handleSelect = useCallback((next: number) => {
    onSelect(next)
    setOpen(false)
  }, [onSelect])

  const title = `${label}: ${current?.label ?? ''}`

  return (
    <div className={cn('control', 'value-picker-control')} data-graph-type="control" title={title}>
      <a className="icon" onClick={handleOpen}>
        <Icon aria-hidden />
        {current ? <span className="value-picker-badge">{current.badge}</span> : null}
        <span className="sr-only">{title}</span>
      </a>
      <Modal open={open} onClose={handleClose} className="value-picker-modal">
        <div className="modal-header">
          <h2 className="modal-title">{label}</h2>
          <CloseButton onClick={handleClose} />
        </div>
        <ul className="value-picker-list">
          {options.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                className={cn('value-picker-item', { selected: opt.value === value })}
                onClick={() => handleSelect(opt.value)}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  )
}

export default ValuePickerControl
